import { type MaybeRefOrGetter, ref, type Ref, toValue, watch } from 'vue'
import { clamp, type ReadonlyRef, type RefOrGetter } from '@/helpers'
import type { Coordinate } from '@/composables/motion/drag-pincher.ts'
import { getTimeout } from '@/helpers/globals.ts'
import { usePercentOpacity } from '@/composables/motion/opacity.ts'
import type { CssTransitionEasing } from '@/composables/motion/css-transition.ts'

interface SwipeToClose {
    swipe: RefOrGetter<Coordinate>
    willClose: ReadonlyRef<boolean>
    percentClosed: ReadonlyRef<number>
}

interface SwipeToCloseOptions {
    swipe: RefOrGetter<Coordinate>
    isSwiping: RefOrGetter<boolean>
    closeThreshold: RefOrGetter<number>
    close(): void
}

export function useSwipeToClose(options: SwipeToCloseOptions): SwipeToClose {
    const { swipe, isSwiping, closeThreshold, close } = options

    const willClose: Ref<boolean> = ref(false)
    const percentClosed: Ref<number> = ref(0)

    watch([swipe, isSwiping], ([swipe, isSwiping]) => {
        const yAbs = Math.abs(swipe.y)
        const closeThresholdValue = toValue(closeThreshold)

        if (isSwiping && (swipe.x || swipe.y)) {
            willClose.value = yAbs >= closeThresholdValue

            percentClosed.value = clamp(yAbs / closeThresholdValue, 0, 1)
        } else {
            if (willClose.value) {
                close()
                willClose.value = false
            }
            percentClosed.value = 0
        }
    })

    return {
        swipe,
        willClose,
        percentClosed,
    }
}

interface SwipeToCloseOpacityTransformConfig {
    endOpacity?: MaybeRefOrGetter<number>
    transitionTime?: MaybeRefOrGetter<number>
    transitionEasing?: MaybeRefOrGetter<CssTransitionEasing>
    translateMode?: MaybeRefOrGetter<'x' | 'y' | 'xy'>
}

export function useSwipeToCloseStyle(
    swiper: SwipeToClose,
    config?: SwipeToCloseOpacityTransformConfig
): ReadonlyRef<string | undefined> {
    const { percentClosed, swipe } = swiper
    const withDefaults: Required<SwipeToCloseOpacityTransformConfig> = {
        endOpacity: 0.2,
        transitionTime: 200,
        transitionEasing: 'ease-in-out',
        translateMode: 'xy',
        ...config,
    }
    const { endOpacity, transitionTime, transitionEasing, translateMode } =
        withDefaults

    const style: Ref<string | undefined> = ref(undefined)

    const timeout = getTimeout()
    let removeTimeoutId: number = 0

    const opacity = usePercentOpacity({
        percent: percentClosed,
        endOpacity: () => toValue(endOpacity),
    })

    if (timeout) {
        const { setTimeout, clearTimeout } = timeout

        watch([percentClosed, swipe], ([percentClosed, swipe]) => {
            const transitionTimeValue = toValue(transitionTime)
            const translateModeValue = toValue(translateMode)

            if (percentClosed) {
                clearTimeout(removeTimeoutId)

                const opacityStyle = `opacity: ${opacity.value.toFixed(2)}`

                let transform: string
                if ('x' === translateModeValue) {
                    transform = `transform: translateX(${swipe.x}px)`
                } else if ('y' === translateModeValue) {
                    transform = `transform: translateY(${swipe.y}px)`
                } else {
                    transform = `transform: translate3d(${swipe.x}px, ${swipe.y}px, 0px)`
                }

                style.value = `${opacityStyle}; ${transform};`
            } else {
                const easing = toValue(transitionEasing)
                const time = `${(transitionTimeValue / 1000).toFixed(3)}s`
                const transition = `transition: opacity ${time} ${easing}, transform ${time} ${easing}`

                let transform: string
                if ('x' === translateModeValue) {
                    transform = `transform: translateX(0px)`
                } else if ('y' === translateModeValue) {
                    transform = `transform: translateY(0px)`
                } else {
                    transform = `transform: translate3d(0px, 0px, 0px)`
                }

                style.value = `opacity: 1; ${transform}; ${transition};`

                removeTimeoutId = setTimeout(() => {
                    style.value = undefined
                }, transitionTimeValue)
            }
        })
    }

    return style
}
